import React from 'react'
import { Box, Card, Stack, HStack } from '@chakra-ui/react'

type TooltipEntry = {
  color?: string
  name?: string
  dataKey?: string | number
  value?: number | string | null
  // Recharts adds the original datum here:
  payload?: Record<string, unknown>
}

type TooltipContentProps = {
  active?: boolean
  label?: string | number
  payload?: TooltipEntry[]
  /** When true, values from Recharts (fractions) will be shown as percentages */
  percentMode?: boolean
  /** When true and percentMode is on, show "raw (XX %)" instead of just "XX %" */
  showRawInPercentMode?: boolean
}

const ChakraTooltip: React.FC<TooltipContentProps> = ({
  active,
  payload,
  label,
  percentMode = false,
  showRawInPercentMode = false,
}) => {
  if (!active || !payload || payload.length === 0) return null

  const hasLabel = label !== undefined && label !== null && String(label).trim() !== ''

  const formatPercent = (v: number) => `${Math.round(v * 100)} %`

  return (
    <Card.Root>
      <Card.Body>
      {hasLabel && <Card.Description>{String(label)}</Card.Description>}
      <Stack gap={1}>
        {payload.map((p, i) => {
          const name = (p.name ?? p.dataKey) as React.ReactNode

          // value from Recharts for the hovered segment
          const numVal =
            typeof p.value === 'number'
              ? p.value
              : p.value != null
                ? Number(p.value as string)
                : NaN

          // try to get the original raw value from the datum
          let raw: unknown = undefined
          if (p?.payload && typeof p.dataKey === 'string') {
            raw = (p.payload as Record<string, unknown>)[p.dataKey]
          }

          let displayValue: React.ReactNode
          if (percentMode && Number.isFinite(numVal)) {
            if (showRawInPercentMode && typeof raw === 'number') {
              displayValue = `${raw} (${formatPercent(numVal)})`
            } else {
              displayValue = formatPercent(numVal)
            }
          } else {
            // fallback to the original value
            if (typeof p.value === 'number') {
              displayValue = p.value.toFixed(2)
            } else if (!isNaN(Number(p.value))) {
              displayValue = Number(p.value).toFixed(2)
            } else {
              displayValue = String(p.value ?? '')
            }
          }

          return (
            <HStack key={i} justify="space-between">
              <HStack>
                <Box
                  boxSize={2}
                  borderRadius="full"
                  bg={(p.color as string) ?? (p as any)?.payload?.fill ?? 'blue.solid'}
                />
                <Card.Description>{name}</Card.Description>
              </HStack>
              <Card.Title>{displayValue}</Card.Title>
            </HStack>
          )
        })}
      </Stack>
      </Card.Body>
    </Card.Root>
  )
}

export default ChakraTooltip
